import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { headers } from "../../../App/Axios"
import { RoleType } from "../../../App/enums"
import HTTPService from "../../../App/HTTPService"
import { selectTranslations } from "../../../App/i18n/i18nSlice"
import { RootState } from "../../../App/store"
import "../../../Styles/main.scss"
import SponsorCard from "../../Shared/Components/SponsorCard"
import useWindowSize from "../../Shared/Hooks/useWindowSize"
import ArticleCard from "../../Shared/ArticleCard/ArticleCard"
import { useNavigate, useParams } from "react-router"
import { LoadingAnimation, SearchIcon } from "../../Shared/Components/SvgIcons"
import UserDashboardButtons from "./UserDashboardButtons"
import FeedContainer from "../../Shared/Components/FeedContainer"
import { setRoomPostsData } from "../../Room/RoomPost/RoomPostSlice"
import FeedTextBox from "../../Shared/Components/FeedTextBox"
import TutorialBubble from "../../Shared/Components/TutorialBubble"
import LocationModal from "./LocationModal"
import { usePullToRefresh } from "../../../App/Helpers/usePullToRefresh"
import { PullToRefreshIndicator } from "../../Shared/Components/PullToRefreshIndicator"

function UserDashboardFeed(props: { hasArticleCard: boolean }) {
    const userDataState = useSelector((state: RootState) => state.userDataSlice)
    const translation = useSelector(selectTranslations)
    const posts = useSelector((state: RootState) => state.RoomPostsSlice.roomPostsSlice.posts)
    const [isLoading, setIsLoading] = useState(true)
    const { id } = useParams()
    const dispatch = useDispatch()
    const screenWidth = useWindowSize()
    const breakpoint = 1023
    const navigate = useNavigate()
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    const [pageNum, setPageNum] = useState(1)
    const [hasNextPage, setHasNextPage] = useState(false)
    const bottom = useRef<HTMLDivElement>(null)
    const scrollContainer = useRef<HTMLDivElement>(null)
    const container = useRef<HTMLDivElement>(null)

    // Pull-to-Refresh
    const handleRefresh = async () => {
        try {
            const response = await GetFeedItemRequest(1);
            if (response.status === 200) {
                dispatch(setRoomPostsData(response.data.results));
                setHasNextPage(Boolean(response.data.next));
                setPageNum(1);
            }
        } catch (error) {
            console.error('Refresh error:', error);
        }
    };

    const { isPulling, isRefreshing, pullDistance, progress } = usePullToRefresh(
        scrollContainer as any,
        {
            onRefresh: handleRefresh,
            enabled: screenWidth < breakpoint, // Only on mobile
        }
    );

    const GetFeedItemRequest = (pageNumber: number) => {
        return HTTPService.get(`/feed?page=${pageNumber}`, headers)
    }

    useEffect(() => {
        let isMounted = true

        if (userDataState && userDataState!.id !== null && userDataState!.id !== undefined) {
            setIsLoading(true)
            GetFeedItemRequest(pageNum)
                .then(response => {
                    if (isMounted && response.status === 200) {
                        setIsLoading(false)
                        dispatch(setRoomPostsData(response.data.results))
                        setHasNextPage(Boolean(response.data.next))

                    }
                })
                .catch(error => {
                    console.log(error)
                })
        }
    }, [dispatch, userDataState])

    useEffect(() => {
        const lockClassName = "dashboard-feed-scroll-lock"
        const isMobile = screenWidth < breakpoint

        if (isMobile) {
            document.body.classList.add(lockClassName)
            document.documentElement.classList.add(lockClassName)
        } else {
            document.body.classList.remove(lockClassName)
            document.documentElement.classList.remove(lockClassName)
        }

        return () => {
            document.body.classList.remove(lockClassName)
            document.documentElement.classList.remove(lockClassName)
        }
    }, [screenWidth, breakpoint])

    const onSeeAllEventsClick = () => {
        switch (role) {
            case RoleType.ORGANIZATION:
                navigate(`/all-premium-events/${id}`)
                break
            case RoleType.USER:
                navigate(`/all-premium-events`)
                break
            default:
                break
        }
    }

    const onScroll = () => {
        if (scrollContainer.current) {
            const { scrollTop, scrollHeight, clientHeight } = scrollContainer.current
            if (Math.ceil(scrollTop + clientHeight + 1) >= scrollHeight && hasNextPage && !isLoading) {
                setIsLoading(true)
                const controller = new AbortController()
                const { signal } = controller

                GetFeedItemRequest(pageNum + 1)
                    .then(response => {
                        if (response.status === 200) {
                            dispatch(setRoomPostsData([...posts, ...response.data.results]))
                            setHasNextPage(Boolean(response.data.next))
                            var topPos = bottom.current!.offsetTop
                            container.current!.scrollTop = topPos - 10
                            setPageNum(pageNum + 1)
                            setIsLoading(false)
                        }
                    })
                    .catch(error => {
                        if (signal.aborted) return
                        console.log(error)
                        setIsLoading(false)
                    })
                return () => controller.abort()
            }
        }
    }

    return (
        <div className="dashboard-feed" ref={container}>
                <LocationModal open={!userDataState.location }/>
            <div className="dashboard-feed__header">
                <div className="dashboard-feed__text">
                    <UserDashboardButtons />
                </div>
            </div>
            <FeedTextBox compactOnDesktop={true} hideCompactTrigger={true} />
            
            <PullToRefreshIndicator 
                isPulling={isPulling}
                isRefreshing={isRefreshing}
                pullDistance={pullDistance}
                progress={progress}
            />
            
            <div className={`dashboard-feed__content`} data-tutorial-anchor="user-feed" onScroll={onScroll} ref={scrollContainer}>
            <TutorialBubble text={translation.tutorial_user_feed} targetSelector='[data-tutorial-anchor="user-feed"]' placement="top" visible={userDataState.tutorial_progress === 2} hasNext={true} />
            {userDataState.tutorial_progress === 2 && (
                <div className="dashboard-feed__tutorial-active"/>
            )
            }
                {posts?.length === 0 && !isLoading ? (
                    <div className="dashboard-feed__header dashboard-feed__header--no-content-card">
                        <p className="dashboard-feed__text dashboard-feed__text--width-90 dashboard-feed__text--font-weight-500 dashboard-feed__text--margin-top-1">
                            {translation.noJoinedRooms1}{" "}
                            <span className="dashboard-feed__text dashboard-feed__text--link" onClick={() => navigate(`/rooms/`)}>
                                {" "}
                                {translation.roomSearch}
                            </span>{" "}
                            {translation.noJoinedRooms2}
                        </p>
                    </div>
                ) : (
                    <FeedContainer type={RoleType.USER} isLoading={isLoading} />
                )}
                {posts!.length > 0 && isLoading && (
                    <div style={{ width: "100%", display: "flex" }}>
                        <LoadingAnimation type="post" />
                    </div>
                )}
                {props.hasArticleCard && screenWidth < breakpoint && <ArticleCard />}
                {screenWidth >= breakpoint && (
                    <>
                        <div className="organization-dashboard-feed__sponsor-title ">
                            {translation.sponsoredText}
                            <div onClick={onSeeAllEventsClick} className="main-layout__sponsored-icon">
                                <SearchIcon color="dark-blue" />
                            </div>
                        </div>
                        <div className="dashboard-feed__sponsor-card">
                            <SponsorCard />
                        </div>
                    </>
                )}
                <div style={{ float: "left", clear: "both" }} ref={bottom}></div>
            </div>
        </div>
    )
}

export default UserDashboardFeed
